import { useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import { useAuth } from "../../context/auth/AuthContextProvider";
import { useLocation } from "../../context/location/useLocation";
import VisitEvent from "../../analytics/VisitEvent";
import HTTPService from "../../services/HTTPService/HTTPService";

const buildVisitEvent = (
	route: string,
	method: string,
	options: {
		userId?: string;
		query?: string;
		language?: string;
		entity?: "partner" | "client";
		entityId?: string;
		entityUserId?: string;
		country?: string;
	}
): VisitEvent => {
	const userAgent =
		typeof navigator !== "undefined" ? navigator.userAgent : undefined;
	const referer =
		typeof document !== "undefined" && document.referrer ? document.referrer : undefined;
	return {
		createdAt: new Date(),
		route,
		query: options.query,
		method,
		userId: options.userId,
		userAgent,
		referer,
		country: options.country,
		language: options.language,
		entity: options.entity,
		entityId: options.entityId,
		entityUserId: options.entityUserId,
	};
};

const AnalyticsTracker = () => {
	const router = useRouter();
	const { user, client, partner } = useAuth();
	const { location } = useLocation();

	const countryCode = useMemo(() => {
		if (!location?.countryCode) return undefined;
		return location.countryCode.toUpperCase();
	}, [location?.countryCode]);

	useEffect(() => {
		if (!router.isReady) return;

		const computeEntity = () => {
			// Determine entity based on route or authenticated profile
			// Prefer explicit partner route param if present
			const asPath = router.asPath || "";
			const path = router.pathname || "";
			let entity: "partner" | "client" | undefined = undefined;
			let entityId: string | undefined = undefined;
			let entityUserId: string | undefined = undefined;

			// Routes carrying partner id (e.g., /partner/[id], /booking/[id])
			const queryId = router.query?.id;
			if (typeof queryId === "string" && (path.startsWith("/partner/") || path.startsWith("/booking/"))) {
				entity = "partner";
				entityId = queryId;
			} else if (partner?._id) {
				entity = "partner";
				entityId = partner._id;
				entityUserId = partner.userId || undefined;
			} else if (client?.userId) {
				entity = "client";
				entityId = client.userId;
				entityUserId = client.userId;
			}
			return { entity, entityId, entityUserId, asPath };
		};

		const post = (asPathValue: string) => {
			const [pathOnly, afterQuestion] = (asPathValue || "").split("?");
			const queryOnly = afterQuestion ? afterQuestion.split("#")[0] : undefined;
			// derive language from first path segment
			const firstSegment = (pathOnly || "/").split("/").filter(Boolean)[0] || "";
			const derivedLanguage =
				/^[a-zA-Z]{2}$/.test(firstSegment) ? firstSegment.toLowerCase() : "en";
			const { entity, entityId, entityUserId } = computeEntity();
			const event = buildVisitEvent(pathOnly || "/", "GET", {
				userId: user?._id,
				query: queryOnly,
				language: derivedLanguage,
				entity,
				entityId,
				entityUserId,
				country: countryCode,
			});
			// Fire and forget; ignore errors
			HTTPService.postVisitEvent(event).catch(() => undefined);
		};

		// Initial page load
		post(router.asPath);

		// Subsequent route changes
		const handleRouteChange = (url: string) => {
			post(url);
		};

		router.events.on("routeChangeComplete", handleRouteChange);
		return () => {
			router.events.off("routeChangeComplete", handleRouteChange);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		router.isReady,
		// Track changes that meaningfully affect payload for subsequent route changes
		user?._id,
		partner?._id,
		partner?.userId,
		client?.userId,
		countryCode,
	]);

	return null;
};

export default AnalyticsTracker;


